export const SHOP_VANITY_HOST_ENV =
  (typeof window !== 'undefined' && (window as any).__FARMERSHUB_VANITY_HOST__) ||
  '';

const DEFAULT_VANITY_BASE = 'farmershub.farm';

const isBrowser = () => typeof window !== 'undefined' && typeof window.location !== 'undefined';

const currentHost = (): string | null => {
  if (!isBrowser()) return null;
  return window.location.hostname || null;
};

const currentProtocol = (): string => {
  if (!isBrowser()) return 'https:';
  return window.location.protocol || 'https:';
};

const currentPort = (): string | null => {
  if (!isBrowser()) return null;
  const p = window.location.port;
  if (!p) return null;
  if (p === '443' && (window.location.protocol || '') === 'https:') return null;
  if (p === '80' && (window.location.protocol || '') === 'http:') return null;
  return p;
};

const extractVanityHostFromEnv = (): string | null => {
  const v = String(SHOP_VANITY_HOST_ENV || '').trim();
  if (!v) return null;
  return v.replace(/^https?:\/\//i, '').replace(/\/+$/, '');
};

const deriveVanityBaseHost = (): string => {
  const env = extractVanityHostFromEnv();
  if (env) return env;
  // In local dev the current host is probably localhost / 127.0.0.1; fall back to
  // the branded display host but keep the app link pointing to the internal route.
  const host = currentHost();
  if (host && /\.[a-z]{2,}$/i.test(host)) {
    const parts = host.split('.');
    if (parts.length >= 3) {
      return parts.slice(-2).join('.');
    }
    return host;
  }
  return DEFAULT_VANITY_BASE;
};

const toSlug = (input: string): string => {
  let s = String(input || '').toLowerCase().trim();
  s = s.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  s = s.replace(/[^a-z0-9]+/g, '-');
  s = s.replace(/(^-+|-+$)/g, '');
  if (!s) return '';
  return s.length > 80 ? s.slice(0, 80) : s;
};

export const buildShopInternalLink = (
  vanitySlug: string,
  opts?: { fallbackShopId?: string; utmSource?: string; utmMedium?: string }
): string => {
  let slug = toSlug(vanitySlug);
  if (!slug && opts?.fallbackShopId) slug = String(opts.fallbackShopId);
  if (!slug) return '/marketplace';
  let href = `/shop/${encodeURIComponent(slug)}`;
  const qs: Array<string> = [];
  if (opts?.utmSource) qs.push(`utm_source=${encodeURIComponent(opts.utmSource)}`);
  if (opts?.utmMedium) qs.push(`utm_medium=${encodeURIComponent(opts.utmMedium)}`);
  if (qs.length) href = `${href}?${qs.join('&')}`;
  return href;
};

// Build an absolute path-style URL for share/copy/preview targets.
// Display text may still use buildShopVanityDisplayUrl() for the branded vanity format;
// this helper NEVER returns a subdomain URL — it always contains /shop/:slug as path,
// so links work reliably without wildcard DNS and work inside mobile WebViews / native-app
// deep links with path-only schemes.
export const buildShopShareableAbsoluteUrl = (
  vanitySlug: string,
  opts?: { fallbackShopId?: string; forceHost?: string; utmSource?: string; utmMedium?: string }
): string => {
  const internal = buildShopInternalLink(vanitySlug, {
    fallbackShopId: opts?.fallbackShopId,
    utmSource: opts?.utmSource,
    utmMedium: opts?.utmMedium,
  });
  const host = (() => {
    if (opts?.forceHost) return opts.forceHost;
    if (!isBrowser()) return DEFAULT_VANITY_BASE;
    const base = window.location.origin || `${currentProtocol()}//${currentHost() || DEFAULT_VANITY_BASE}`;
    return base;
  })();
  const path = internal.startsWith('/') ? internal : `/${internal}`;
  return `${host}${path}`;
};

// display text ONLY helper (deprecated for navigational hrefs/clipboard targets — use
// buildShopInternalLink or buildShopShareableAbsoluteUrl instead). Kept because the
// UX pattern of "show vanity as text, copy path as URL" helps users remember the
// branded shortlink while still getting clicks that work reliably.
export const buildShopVanityDisplayUrl = (
  vanitySlug: string,
  opts?: { utmSource?: string; utmMedium?: string; forceHost?: string }
): string => {
  const slug = toSlug(vanitySlug);
  const base = opts?.forceHost || deriveVanityBaseHost();
  const port = currentPort();
  const protocol = currentProtocol();
  const qs: Array<string> = [];
  if (opts?.utmSource) qs.push(`utm_source=${encodeURIComponent(opts.utmSource)}`);
  if (opts?.utmMedium) qs.push(`utm_medium=${encodeURIComponent(opts.utmMedium)}`);
  const qsStr = qs.length ? `?${qs.join('&')}` : '';
  const host = slug ? `${slug}.${base}` : base;
  const portStr = port && /^localhost|127\.0\.0\.1$/.test(base) ? `:${port}` : '';
  if (!slug) return `${protocol}//${host}${portStr}${qsStr}`;
  return `${protocol}//${host}${portStr}/`;
};

export const copyTextToClipboard = async (text: string): Promise<boolean> => {
  if (typeof navigator !== 'undefined' && navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // fall through to fallback
    }
  }
  if (typeof document !== 'undefined') {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'absolute';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return !!ok;
    } catch {
      return false;
    }
  }
  return false;
};

export const shareOrCopyUrl = async (url: string, title?: string, text?: string): Promise<{ copied: boolean; openedShare: boolean }> => {
  if (
    typeof navigator !== 'undefined' &&
    navigator.share &&
    /android|iphone|ipad|ipod|mobile/i.test(typeof navigator.userAgent === 'string' ? navigator.userAgent : '')
  ) {
    try {
      await navigator.share({ title, text, url });
      return { copied: false, openedShare: true };
    } catch {
      /* user cancelled / not allowed */
    }
  }
  const copied = await copyTextToClipboard(url);
  return { copied, openedShare: false };
};

export const deriveShopLabelFromSlug = (vanitySlug: string): string => {
  const slug = toSlug(vanitySlug);
  if (!slug) return 'Your shop';
  return slug
    .split('-')
    .map((p) => (p ? p.charAt(0).toUpperCase() + p.slice(1) : p))
    .join(' ');
};

export const shopSlugUtils = { toSlug };
